import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { GoldDots } from "@/components/Brand";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { errMsg, formatDate, useApi } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { Panel, PrivacyNote, Row } from "@/components/admin/AdminUI";

const Sessions = ({ uid }) => {
  const api = useApi();
  const [rows, setRows] = useState(null);
  const load = () => api.get(`/admin/people/${uid}/sessions`).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [uid]); // eslint-disable-line
  const revoke = async (session_id) => {
    try { const { data } = await api.post(`/admin/people/${uid}/sessions/revoke`, { session_id }); toast.success(`Signed out of ${data.revoked} session(s)`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!rows) return <p className="text-sm text-ink-soft">Loading sessions</p>;
  return (
    <div data-testid={`sessions-${uid}`}>
      {rows.length === 0 ? <p className="text-sm text-ink-soft">No active sessions.</p> : (
        <ul className="space-y-2">
          {rows.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-alabaster px-4 py-3 text-sm">
              <span>{s.device}{s.place && ` · ${s.place}`} · last active {formatDate(new Date(s.last_active_at).toISOString(), { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              <button className="btn-quiet text-sm" onClick={() => revoke(s.id)} data-testid={`revoke-session-${s.id}`}>Sign out</button>
            </li>
          ))}
        </ul>
      )}
      {rows.length > 1 && <button className="btn-outline !min-h-[40px] !py-2 text-sm mt-3" onClick={() => revoke(null)} data-testid={`revoke-all-${uid}`}>Sign out everywhere</button>}
    </div>
  );
};

const Person = ({ p, roles, reload }) => {
  const api = useApi();
  const { me } = useMe();
  const [open, setOpen] = useState(false);
  const self = p.user_id === me.user_id;
  const call = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <li className="border-b border-rule last:border-0" data-testid={`person-${p.user_id}`}>
      <button className="w-full flex items-center justify-between gap-4 py-4 text-left" onClick={() => setOpen(!open)} aria-expanded={open} data-testid={`person-toggle-${p.user_id}`}>
        <span className="min-w-0">
          <span className="block font-medium text-charcoal truncate">{p.name || "Not yet named"}{p.suspended && <span className="ml-2 text-xs text-warn">Suspended</span>}</span>
          <span className="block text-sm text-ink-soft truncate">{p.email} · {p.complimentary ? "Honorary" : p.is_member ? "Member" : "Free"}{p.role === "admin" ? " · Admin" : ""}{p.migrated ? " · go-native-2" : ""}</span>
        </span>
        <ChevronDown size={18} className="shrink-0 text-charcoal" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .4s ease" }} />
      </button>
      {open && (
        <div className="pb-6 fade-in space-y-5">
          <div>
            <Row label="Joined">{formatDate(p.created_at)}</Row>
            <Row label="Last sign-in">{p.last_sign_in_at ? formatDate(p.last_sign_in_at) : p.sign_in_count ? "Not recorded" : "Not yet"} · {p.sign_in_count} sign-ins</Row>
            <Row label="Membership">
              <span className="inline-flex flex-wrap items-center justify-end gap-3">
                {p.complimentary ? "Honorary" : p.is_member ? "Paid member" : "Free"}
                {(!p.is_member || p.complimentary) && (
                  <button className="btn-quiet text-sm" onClick={() => call(() => api.post("/admin/complimentary", { email: p.email, on: !p.complimentary }), p.complimentary ? "Honorary membership removed" : "Honorary membership given")} data-testid={`comp-toggle-${p.user_id}`}>
                    {p.complimentary ? "Remove" : "Give honorary membership"}
                  </button>
                )}
              </span>
            </Row>
            <Row label="Profile and terms">{p.profile_complete ? "Complete" : "Not finished"} · {p.consented ? "Terms agreed" : "Terms not yet agreed"}</Row>
            <Row label="Plans">{p.active_plans} active · {p.completed_plans} completed</Row>
            <Row label="Role">
              <select className="field !py-1.5 !w-32" value={p.role} disabled={self} onChange={(e) => call(() => api.patch(`/users/${p.user_id}/role`, { role: e.target.value }), "Role updated")} data-testid={`role-select-${p.user_id}`}>
                {roles.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Row>
          </div>
          <div>
            <p className="text-sm font-medium text-charcoal mb-2">Active sessions</p>
            <Sessions uid={p.user_id} />
          </div>
          {!self && (
            <div className="flex flex-wrap gap-5 pt-2">
              <button className="text-sm underline underline-offset-4 text-charcoal" onClick={() => call(() => api.post(`/admin/people/${p.user_id}/suspend`, { suspended: !p.suspended }), p.suspended ? "Account restored" : "Account suspended")} data-testid={`suspend-${p.user_id}`}>
                {p.suspended ? "Restore account" : "Suspend account"}
              </button>
              <ConfirmDialog title="Delete this account?" body="Their account and personal details are deleted. Only anonymised plan content that can't be linked to them is kept. This can't be undone."
                confirmLabel="Delete account" onConfirm={() => call(() => api.delete(`/admin/people/${p.user_id}`), "Account deleted")} testId={`delete-person-${p.user_id}`}
                trigger={<button className="text-sm text-warn underline underline-offset-4" data-testid={`delete-person-btn-${p.user_id}`}>Delete account</button>} />
            </div>
          )}
        </div>
      )}
    </li>
  );
};

const CompByEmail = ({ onDone }) => {
  const api = useApi();
  const [email, setEmail] = useState("");
  const give = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/complimentary", { email, on: true });
      toast.success(data.account_exists ? "Honorary membership given" : "Saved. It starts as soon as they sign up.");
      setEmail(""); onDone();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={give} className="rounded-xl bg-alabaster p-4 mb-5" data-testid="comp-by-email">
      <p className="text-sm font-medium text-charcoal mb-2">Give an honorary membership</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input type="email" required className="field" placeholder="Their email address" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="comp-email-input" />
        {email.includes("@") && <button className="btn-charcoal shrink-0 fade-in" data-testid="comp-give-btn">Give membership</button>}
      </div>
      <p className="text-xs text-ink-soft mt-2">Works for people already here, and for people who haven't signed up yet. No payment is taken.</p>
    </form>
  );
};

export const AdminPeople = () => {
  const api = useApi();
  const [people, setPeople] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/people").then((r) => setPeople(r.data));
  useEffect(() => { load(); }, [api]); // eslint-disable-line
  if (!people) return <div className="py-16 flex justify-center"><GoldDots /></div>;
  const shown = people.filter((p) => `${p.name} ${p.email}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <Panel title="People and sessions" sub="Look after accounts: roles, active sessions, suspending and deleting." testId="admin-people">
      <CompByEmail onDone={load} />
      <input className="field mb-2" placeholder="Search by name or email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="people-search-input" />
      <p className="text-sm text-ink-soft mb-2">{shown.length} of {people.length} people</p>
      <ul>{shown.map((p) => <Person key={p.user_id} p={p} roles={["admin", "member"]} reload={load} />)}</ul>
      <PrivacyNote />
    </Panel>
  );
};
